import React, { useState, useEffect, useCallback } from "react";
import { TextField, debounce } from '@mui/material';
import { Autocomplete } from '@material-ui/lab'
import useToast from '@fuse/hooks/useToast'
import { useDispatch } from 'react-redux';

const SearchDropDownListPaginationComponent = ({
    listCall,
    label,
    apiParams,
    searchEnabled,
    emitItem,
    defaultValue
}) => {
    const [page, setPage] = useState(0);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationObject, setPaginationObject] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [position, setPosition] = useState(0);
    const [mounted, setMounted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(defaultValue ?? '');
    const [actualOptions, setActualOptions] = useState([]);
    const [listboxNode, setListboxNode] = useState("");
    const dispatch = useDispatch()
    const toast = useToast(dispatch)

    useEffect(() => {
        setMounted(true);
    }, []);
    const getOptionsDelayed = useCallback(
        debounce((query, apiParam, cb) => {
            setOptions([]);
            fetchAPI(query, apiParam).then((response) => {
                cb(response);
            });
        }, 500),
        []
    );

    useEffect(() => {
        setSelectedValue(defaultValue ?? '');
    }, [defaultValue]);

    useEffect(() => {
        if (mounted && isOpen) {
            setLoading(true);
            getOptionsDelayed(searchQuery, apiParams, (response) => {
                const optionsList = response.data.data.data.map((opts) => {
                    const name = `${opts?.serial_number}`
                    return {
                        id: opts.id,
                        label: opts?.name ? opts.name : name,
                        key: `${opts.id}|${opts?.name ? opts.name : name}`,
                    };
                });
                setOptions(optionsList);
                setActualOptions(response.data.data.data)
                setPaginationObject(response.data.data.meta);
                setPage(page + 1);
                setLoading(false);
            });
        }
    }, [searchQuery, getOptionsDelayed, isOpen]);

    // eslint-disable-next-line consistent-return
    const fetchAPI = async (query, apiParam = {}) => {
        try {
            const queryParams = {
                ...apiParams,
                page: page + 1,
            };
            if (query && searchEnabled) {
                queryParams.search = query;
            }
            const response = await listCall(queryParams);
            return response;
        } catch (err) {
            console.log(err);
        }
    };
    const fetchOptions = async (query) => {
        try {
            const queryParams = {
                ...apiParams,
                page: page + 1,
            };
            if (query && searchEnabled) {
                queryParams.search = query;
            }
            setLoading(true);
            const response = await listCall(queryParams);
            setPage(page + 1);
            const optionsList = response.data.data.data.map((opts) => {
                const name = `${opts?.serial_number}`
                return {
                    id: opts.id,
                    label: opts?.name ? opts?.name : name,
                    key: `${opts.id}|${opts.name ? opts.name : name}`,
                };
            });
            setPaginationObject(response.data.data.meta);
            setActualOptions((prev) => [...prev, ...response.data.data.data])
            setOptions((prevState) => {
                return [...prevState, ...optionsList];
            });
            setLoading(false);
        } catch (e) {
            setLoading(false);
            toast.error("Encountered problem with fetching records.");
        }
    };
    useEffect(() => {
        if (listboxNode !== "" && listboxNode !== undefined) {
            listboxNode.scrollTop = position;
        }
    }, [position, listboxNode]);
    const handleScroll = async (event) => {
        setListboxNode(event.currentTarget);
        const x = listboxNode.scrollTop + listboxNode.clientHeight;
        // only when checking this condition we change the position
        if (listboxNode.scrollHeight - x <= 1) {
            setPosition(x);
            if (options.length !== paginationObject.total && !loading) {
                await fetchOptions();
            }
        }
    };
    return (
        <Autocomplete
            freeSolo
            sx={{ width: 160 }}
            disablePortal
            options={options}
            open={isOpen}
            autoHighlight
            loading={loading}
            value={selectedValue}
            // inputValue={defaultValue || ''}
            //={defaultValue || ''}
            onOpen={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(true);
            }}
            getOptionLabel={(option) => (option ? option?.label ? option.label : option : "")}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onInputChange={(event, newValue) => {
                let dropItem = newValue
                if (newValue.split('|').length > 1) {
                    dropItem = newValue?.split('|')[1]
                }

                setSearchQuery(dropItem);
                if (!newValue) {
                    emitItem(null)
                }
            }}
            onClose={() => {
                setIsOpen(false);
                setOptions([]);
                setPaginationObject([]);
                setLoading(false);
                setPage(0);
            }}
            onChange={(_, newValue) => {
                setSelectedValue(newValue?.label ?? '');
                const itemToSend =
                    newValue !== null
                        ? options.find((_or) => _or.id === newValue.id)
                        : undefined;
                const actualItem = newValue !== null
                    ? actualOptions.find((_or) => _or.id === newValue.id)
                    : undefined;
                emitItem(itemToSend && actualItem ? { ...itemToSend, ...actualItem } : undefined);
            }}
            renderInput={(params) => {
                return (
                    <div ref={params.InputProps.ref}>
                        <TextField
                            {...params}
                            style={{ background: 'white' }}
                            label={label}
                            className='location_field'
                            FormHelperTextProps={{
                                style: {
                                    margin: 0,
                                    backgroundColor: '#f4f4f4',
                                    width: '100%',
                                    fontSize: '12px !important',
                                    fontStyle: 'normal',
                                    fontWeight: '500 !important',
                                    lineHeight: '16px',
                                    color: '#98A2B3 !important',
                                    fontFamily: '"Inter", sans-serif !important',
                                }
                            }}
                            inputProps={{
                                ...params.inputProps,
                                value: params.inputProps.value !== '' ? params.inputProps.value : selectedValue,
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation();
                                    }
                                },
                            }}
                            variant="outlined"
                            fullWidth
                        />
                    </div>
                )
            }}
            ListboxProps={{
                onScroll: handleScroll,
            }}
        />
    );
}

export default SearchDropDownListPaginationComponent